"use client";

import React from "react";
import { motion } from "framer-motion";
import { Lock, Trophy, ShieldCheck, Terminal } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
const categories = [{
  id: "cat-1",
  title: "VPN Basics",
  icon: Lock
}, {
  id: "cat-2",
  title: "Choosing the Right VPN",
  icon: Trophy
}, {
  id: "cat-3",
  title: "Privacy & Security",
  icon: ShieldCheck
}];
const faqs = [{
  id: "q1",
  categoryId: "cat-1",
  question: "What is a VPN and how does it work?",
  answer: "A VPN (Virtual Private Network) is a technology that encrypts your internet connection and routes it through a secure server in a location of your choice. When you connect to a VPN:\n\n1. Your device encrypts all outgoing data\n2. That encrypted data travels to a VPN server\n3. The VPN server decrypts it and sends it to its destination on your behalf\n4. Your real IP address is hidden — websites only see the VPN server's IP address\n\nThe result? Your internet activity becomes private, your location is masked, and your data is protected from hackers, ISPs, governments, and surveillance."
}, {
  id: "q2",
  categoryId: "cat-1",
  question: "Do I really need a VPN?",
  answer: "Yes — more than ever. Here's why:\n\n ISPs legally sell your browsing data to advertisers in many countries\n\n Public Wi-Fi networks are a goldmine for hackers and cybercriminals\n\n Governments and corporations increasingly monitor and restrict online activity\n\n Data brokers build detailed profiles on your browsing habits\n\n Streaming platforms geo-restrict content based on your location\n\nA VPN protects you from all of the above. For anyone who values their privacy, security, or freedom online — a VPN is no longer optional. It's essential."
}, {
  id: "q3",
  categoryId: "cat-1",
  question: "Is using a VPN legal?",
  answer: "In most countries, yes — using a VPN is completely legal. VPNs are widely used by:\n\n Businesses protecting remote workers\n Journalists protecting sources\n Everyday users protecting their privacy\n Travelers accessing home content abroad\n\nHowever, VPN use is restricted or illegal in a small number of countries including:\n\n China\n Russia\n North Korea\n Belarus\n Iran\n Iraq\n Turkmenistan\n Uganda\n\nImportant: A VPN does not make illegal activities legal. Using a VPN to commit crimes is still illegal regardless of your location.\n\nAlways check the laws in your specific country before using a VPN."
}, {
  id: "q4",
  categoryId: "cat-1",
  question: "Will a VPN slow down my internet?",
  answer: "A VPN will cause a small reduction in speed because your traffic is being encrypted and routed through an additional server. However:\n\n Top-tier VPNs like NordVPN and ExpressVPN reduce speeds by less than 10-15% on their best servers\n\n Modern protocols like NordLynx (WireGuard) and Lightway are engineered specifically to minimize speed loss\n\n In some cases — particularly with throttled ISP connections — a VPN can actually INCREASE your speeds\n\nThe speed impact is almost unnoticeable for everyday browsing, streaming, and gaming when using a premium VPN on a nearby server."
}, {
  id: "q5",
  categoryId: "cat-1",
  question: "What is a no-logs VPN policy?",
  answer: "A no-logs (or zero-logs) policy means the VPN provider does not collect, store, or share any records of your online activity. This includes:\n\n Browsing history\n Connection timestamps\n IP addresses\n DNS queries\n Traffic data\n Bandwidth usage\n\nWhy does this matter? If a VPN keeps no logs, there is nothing to hand over to governments, law enforcement, or hackers — even if legally compelled to do so.\n\nVPNGENIX only recommends VPNs with independently audited no-logs policies. All 6 VPNs on our list have been verified by third-party security firms."
}, {
  id: "q6",
  categoryId: "cat-2",
  question: "What is the best VPN?",
  answer: "Based on our independent testing across speed, security, privacy, streaming, and value — NordVPN is our #1 pick.\n\nHere's our complete ranking:\n\n NordVPN — 9.3/10 — Best All-Around\n ExpressVPN — 9.1/10 — Fastest Speeds\n Surfshark — 8.9/10 — Best Value\n4⃣ CyberGhost — 8.7/10 — Best for Streaming\n5⃣ PIA — 8.5/10 — Best for Power Users\n6⃣ ProtonVPN — 8.4/10 — Best for Privacy"
}, {
  id: "q7",
  categoryId: "cat-2",
  question: "What is the best VPN for streaming?",
  answer: "For streaming, we recommend:\n\n CyberGhost — Dedicated streaming servers optimized for Netflix, Disney+, Amazon Prime, BBC iPlayer, Hulu & more\n\n NordVPN — Consistently unblocks every major streaming platform with fast, buffer-free performance\n\n ExpressVPN — Lightning-fast speeds make it perfect for HD and 4K streaming worldwide\n\nAll three reliably bypass geo-restrictions and deliver buffer-free streaming in HD and 4K quality."
}, {
  id: "q8",
  categoryId: "cat-2",
  question: "What is the best VPN for gaming?",
  answer: "For gaming, the most important factors are low latency (ping), fast speeds, and DDoS protection. Our top picks are:\n\n NordVPN — Ultra-low latency with NordLynx protocol + DDoS protection\n\n ExpressVPN — Consistently fast worldwide speeds reduce lag\n\n Surfshark — Great performance + affordable for long gaming sessions\n\nA gaming VPN also lets you:\n Access games before your regional release\n Connect to servers in other regions\n Protect against DDoS attacks in competitive gaming\n Reduce ISP throttling during peak hours"
}, {
  id: "q9",
  categoryId: "cat-2",
  question: "What is the best cheap VPN?",
  answer: "You don't need to spend a lot to get solid VPN protection. Our best budget picks are:\n\n Private Internet Access — From $2.03/mo\n   Unlimited devices + 35,000+ servers\n\n CyberGhost — From $2.19/mo\n   9,700+ servers + 45-day money-back\n\n Surfshark — From $2.49/mo\n   Unlimited devices + premium features\n\nAll three offer excellent security, fast speeds, and verified no-logs policies at a fraction of the cost of premium VPNs.\n\n Warning: Avoid free VPNs. Most free VPN providers make money by logging and selling your data — the exact opposite of what a VPN should do."
}, {
  id: "q10",
  categoryId: "cat-2",
  question: "Should I use a free VPN?",
  answer: "We strongly advise against free VPNs. Here's why:\n\n Most free VPNs log and sell your browsing data to advertisers\n\n Many inject ads and tracking cookies into your browser\n\n Free VPNs have limited servers causing severe speed throttling\n\n Weak or outdated encryption leaves you vulnerable to attacks\n\n Some free VPNs contain malware\n\nThe saying holds true: if you're not paying for the product, YOU are the product.\n\nOur cheapest recommended VPN — Private Internet Access — starts at just $2.03/month. That's genuine privacy protection for less than a cup of coffee. It's worth it."
}, {
  id: "q11",
  categoryId: "cat-3",
  question: "Can my ISP see what I do with a VPN?",
  answer: "No. When you connect to a VPN, your ISP can see:\n\n That you are connected to a VPN server\n The amount of data being transferred\n The VPN server's IP address\n\nBut your ISP CANNOT see:\n\n Which websites you visit\n What you search for\n What files you download\n What you stream or watch\n Any of your actual internet activity\n\nYour traffic is fully encrypted before it even leaves your device — so your ISP only sees an unreadable stream of encrypted data."
}, {
  id: "q12",
  categoryId: "cat-3",
  question: "Can I be tracked if I use a VPN?",
  answer: "A VPN significantly reduces your digital footprint but does not make you 100% anonymous. Here's what a VPN protects against — and what it doesn't:\n\n PROTECTS AGAINST:\n• IP address tracking\n• ISP surveillance\n• Network-level monitoring\n• Public Wi-Fi snooping\n• Geo-location tracking\n\n DOES NOT FULLY PROTECT AGAINST:\n• Browser cookies & fingerprinting\n• Being logged into Google/Facebook\n• Malware on your device\n• Phishing attacks\n• A VPN provider that logs your data\n\nFor maximum anonymity, combine your VPN with a private browser (Firefox/Brave), disable cookies, and use a search engine like DuckDuckGo or ProtonMail."
}, {
  id: "q13",
  categoryId: "cat-3",
  question: "What is the most secure VPN protocol?",
  answer: "VPN protocols determine how your data is encrypted and transmitted. Here's a breakdown:\n\n WireGuard — Best balance of speed and security. Modern, lightweight, and open-source. Our top recommendation.\n\n OpenVPN — Battle-tested and highly secure. Slightly slower but extremely reliable. Industry standard.\n\n IKEv2/IPSec — Excellent for mobile devices. Fast reconnection after switching networks.\n\n PPTP — Outdated and insecure. Avoid at all costs.\n\nMost premium VPNs now use WireGuard or their own WireGuard-based protocol:\n• NordVPN uses NordLynx (based on WireGuard)\n• ExpressVPN uses Lightway (proprietary)\n• Surfshark uses WireGuard natively"
}, {
  id: "q14",
  categoryId: "cat-3",
  question: "What is a VPN kill switch?",
  answer: "A kill switch is a critical security feature that automatically cuts your internet connection if your VPN drops unexpectedly.\n\nWithout a kill switch:\n If your VPN disconnects mid-session, your real IP address is instantly exposed to websites and your ISP\n\nWith a kill switch:\n If your VPN drops, all internet traffic is immediately blocked until the VPN reconnects — ensuring your real IP is never exposed\n\nA kill switch is essential for:\n• Torrenting and P2P file sharing\n• Journalists and activists\n• Anyone handling sensitive data\n• Users in high-censorship countries\n\nAll 6 VPNs recommended by VPNGENIX include a kill switch."
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function FaqCommandCenter() {
  return <section id="faqaccordioncommandcenter" className="dark bg-background py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-5xl h-[500px] bg-primary/5 blur-[120px] rounded-lg pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-primary/5 blur-[100px] rounded-lg pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10 max-w-4xl">
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-primary font-default mb-6 tracking-tight">
            FAQ
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Everything you need to know about securing your digital footprint,
            choosing the right tools, and maintaining absolute privacy online.
          </p>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-100px"
      }} className="space-y-12">
          {categories.map((category, index) => {
          const categoryFaqs = faqs.filter(faq => faq.categoryId === category.id);
          const Icon = category.icon;
          return <motion.div key={category.id} variants={itemVariants} data-index={index} className="relative">
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 text-primary">
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-2xl font-bold text-primary font-default">
                    {category.title}
                  </h3>
                  <div className="flex-1 h-px bg-gradient-to-r from-border to-transparent ml-4" />
                </div>

                <Accordion type="single" collapsible className="w-full space-y-4">
                  {categoryFaqs.map((faq, faqIndex) => <AccordionItem key={faq.id} value={faq.id} data-index={faqIndex} className="border border-border/50 bg-card/40 backdrop-blur-sm rounded-xl overflow-hidden data-[state=open]:border-primary/50 data-[state=open]:bg-card/80 transition-all duration-300 shadow-sm hover:shadow-md">
                      <AccordionTrigger className="px-6 py-5 hover:bg-accent/30 hover:no-underline group">
                        <span className="font-default text-base md:text-lg font-semibold text-left text-foreground group-hover:text-primary transition-colors">
                          {faq.question}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <ScrollArea className="max-h-[350px] w-full">
                          <div className="px-6 pb-6 pt-2 text-muted-foreground whitespace-pre-wrap leading-relaxed text-sm md:text-base">
                            {faq.answer}
                          </div>
                        </ScrollArea>
                      </AccordionContent>
                    </AccordionItem>)}
                </Accordion>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}